// Single source of truth for Indian state/UT GST codes (used by every form with a stateCode).
export interface IndianState {
  code: string;
  name: string;
}

export const INDIAN_STATES: IndianState[] = [
  { code: '01', name: 'Jammu & Kashmir' },
  { code: '02', name: 'Himachal Pradesh' },
  { code: '03', name: 'Punjab' },
  { code: '04', name: 'Chandigarh' },
  { code: '05', name: 'Uttarakhand' },
  { code: '06', name: 'Haryana' },
  { code: '07', name: 'Delhi' },
  { code: '08', name: 'Rajasthan' },
  { code: '09', name: 'Uttar Pradesh' },
  { code: '10', name: 'Bihar' },
  { code: '11', name: 'Sikkim' },
  { code: '12', name: 'Arunachal Pradesh' },
  { code: '13', name: 'Nagaland' },
  { code: '14', name: 'Manipur' },
  { code: '15', name: 'Mizoram' },
  { code: '16', name: 'Tripura' },
  { code: '17', name: 'Meghalaya' },
  { code: '18', name: 'Assam' },
  { code: '19', name: 'West Bengal' },
  { code: '20', name: 'Jharkhand' },
  { code: '21', name: 'Odisha' },
  { code: '22', name: 'Chhattisgarh' },
  { code: '23', name: 'Madhya Pradesh' },
  { code: '24', name: 'Gujarat' },
  { code: '26', name: 'Dadra & Nagar Haveli and Daman & Diu' },
  { code: '27', name: 'Maharashtra' },
  { code: '29', name: 'Karnataka' },
  { code: '30', name: 'Goa' },
  { code: '31', name: 'Lakshadweep' },
  { code: '32', name: 'Kerala' },
  { code: '33', name: 'Tamil Nadu' },
  { code: '34', name: 'Puducherry' },
  { code: '35', name: 'Andaman & Nicobar Islands' },
  { code: '36', name: 'Telangana' },
  { code: '37', name: 'Andhra Pradesh' },
  { code: '38', name: 'Ladakh' },
];

// AntD Select options: "27 · Maharashtra".
export const STATE_OPTIONS = INDIAN_STATES.map((s) => ({ value: s.code, label: `${s.code} · ${s.name}` }));

/** Clean GST code → state name (no code prefix) — for display columns. */
export const STATE_NAME = new Map(INDIAN_STATES.map((s) => [s.code, s.name] as [string, string]));

/**
 * The two-letter postal codes, so a value that arrived as "MH" still reads as Maharashtra.
 *
 * The stored `state_code` is always the GST number (the entity transformer normalises it), but a
 * value that was only ever POSTED — an audit-log payload, an import row, a geocoder hint — can still
 * be the letters, and "State code: MH" tells a reader nothing they wanted. Mirrors the backend's
 * `gst-states.ts` alpha2 column.
 */
export const ALPHA2_TO_CODE: Record<string, string> = {
  JK: '01', HP: '02', PB: '03', CH: '04', UK: '05', HR: '06', DL: '07', RJ: '08', UP: '09', BR: '10',
  SK: '11', AR: '12', NL: '13', MN: '14', MZ: '15', TR: '16', ML: '17', AS: '18', WB: '19', JH: '20',
  OD: '21', CG: '22', MP: '23', GJ: '24', DD: '26', DN: '26', MH: '27', KA: '29', GA: '30', LD: '31',
  KL: '32', TN: '33', PY: '34', AN: '35', TS: '36', AD: '37', LA: '38',
};

/** "27" → Maharashtra, and so does "MH". An unrecognised value is returned as given. */
export const stateName = (code?: string | null) => {
  if (!code) return '';
  const raw = String(code).trim();
  return STATE_NAME.get(raw) ?? STATE_NAME.get(ALPHA2_TO_CODE[raw.toUpperCase()] ?? '') ?? raw;
};

const norm = (s: string) => s.toLowerCase().replace(/[^a-z]/g, '');
const NAME_TO_CODE: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const s of INDIAN_STATES) m[norm(s.name)] = s.code;
  // Aliases for names OSM/Nominatim may return.
  m[norm('NCT of Delhi')] = '07';
  m[norm('National Capital Territory of Delhi')] = '07';
  m[norm('Orissa')] = '21';
  m[norm('Pondicherry')] = '34';
  m[norm('Uttaranchal')] = '05';
  return m;
})();

/** Best-effort map of a state NAME (e.g. from a geocoder) to its 2-digit GST code; undefined if unknown. */
export const stateCodeFromName = (name?: string | null): string | undefined =>
  name ? NAME_TO_CODE[norm(name)] : undefined;
